import type { Matrix2D } from "../geometry";
import type {
  GraphicColourStop,
  GraphicFill,
  GraphicImage,
  GraphicNode,
  GraphicStroke,
  GraphicText,
  GraphicTree,
} from "../graphic-tree";

export type SvgViewBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type SvgSerializerOptions = {
  width: number;
  height: number;
  viewBox?: SvgViewBox;
  unit?: "mm" | "px";
  title?: string;
  description?: string;
  idPrefix?: string;
  rootAttributes?: Readonly<Record<string, string>>;
  resolveAsset?: (assetId: string) => string | undefined;
};

type SerializerContext = {
  gradients: ReadonlyMap<string, string>;
  resolveAsset?: SvgSerializerOptions["resolveAsset"];
};

function formatNumber(value: number) {
  if (Object.is(value, -0) || Math.abs(value) < 1e-10) return "0";
  return Number(value.toFixed(5)).toString();
}

function escapeAttribute(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function escapeText(value: string) {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function attribute(name: string, value: string | number | undefined) {
  if (value === undefined) return "";
  return ` ${name}="${typeof value === "number" ? formatNumber(value) : escapeAttribute(value)}"`;
}

function gradientKey(fill: GraphicFill) {
  if (fill.kind === "solid") return "";
  const points =
    fill.kind === "linear-gradient"
      ? `${formatNumber(fill.from.x)},${formatNumber(fill.from.y)}:${formatNumber(fill.to.x)},${formatNumber(fill.to.y)}`
      : `${formatNumber(fill.centre.x)},${formatNumber(fill.centre.y)}:${formatNumber(fill.radius)}`;
  return `${fill.kind}:${points}:${fill.stops
    .map(
      ({ offset, colour, alpha }) =>
        `${formatNumber(offset)},${colour},${formatNumber(alpha ?? 1)}`,
    )
    .join(";")}`;
}

function collectGradientFills(node: GraphicNode, result: GraphicFill[]) {
  if (node.type === "group") {
    for (const child of node.children) collectGradientFills(child, result);
    return;
  }
  if (
    (node.type === "rect" ||
      node.type === "ellipse" ||
      node.type === "polyline" ||
      node.type === "path") &&
    node.fill &&
    node.fill.kind !== "solid"
  ) {
    result.push(node.fill);
  }
}

function gradientStops(stops: readonly GraphicColourStop[]) {
  return stops
    .map(
      (stop) =>
        `<stop offset="${formatNumber(stop.offset * 100)}%" stop-color="${escapeAttribute(stop.colour)}"${
          stop.alpha === undefined ? "" : attribute("stop-opacity", stop.alpha)
        }/>`,
    )
    .join("");
}

function serializeGradient(fill: GraphicFill, id: string) {
  if (fill.kind === "solid") return "";
  if (fill.kind === "linear-gradient") {
    return `<linearGradient id="${escapeAttribute(id)}" gradientUnits="userSpaceOnUse"${attribute("x1", fill.from.x)}${attribute("y1", fill.from.y)}${attribute("x2", fill.to.x)}${attribute("y2", fill.to.y)}>${gradientStops(fill.stops)}</linearGradient>`;
  }
  return `<radialGradient id="${escapeAttribute(id)}" gradientUnits="userSpaceOnUse"${attribute("cx", fill.centre.x)}${attribute("cy", fill.centre.y)}${attribute("r", fill.radius)}>${gradientStops(fill.stops)}</radialGradient>`;
}

function matrixTransform(transform: Matrix2D | undefined) {
  if (!transform) return "";
  return `matrix(${formatNumber(transform.a)} ${formatNumber(transform.b)} ${formatNumber(transform.c)} ${formatNumber(transform.d)} ${formatNumber(transform.tx)} ${formatNumber(transform.ty)})`;
}

function commonAttributes(node: GraphicNode, extraTransform = "") {
  const transforms = [matrixTransform(node.transform), extraTransform].filter(Boolean).join(" ");
  return `${attribute("id", node.id)}${attribute("class", node.className)}${attribute("opacity", node.alpha)}${
    node.visible === false ? ' display="none"' : ""
  }${attribute("transform", transforms || undefined)}`;
}

function fillAttributes(fill: GraphicFill | undefined, gradients: ReadonlyMap<string, string>) {
  if (!fill) return ' fill="none"';
  if (fill.kind === "solid") {
    return `${attribute("fill", fill.colour)}${attribute("fill-opacity", fill.alpha)}`;
  }
  const id = gradients.get(gradientKey(fill));
  return id ? ` fill="url(#${escapeAttribute(id)})"` : ' fill="none"';
}

function strokeAttributes(stroke: GraphicStroke | undefined) {
  if (!stroke) return ' stroke="none"';
  return `${attribute("stroke", stroke.colour)}${attribute("stroke-width", stroke.width)}${attribute(
    "stroke-opacity",
    stroke.alpha,
  )}${attribute("stroke-dasharray", stroke.dash?.join(" "))}${attribute(
    "stroke-linecap",
    stroke.cap,
  )}${attribute("stroke-linejoin", stroke.join)}`;
}

function serializeRootAttributes(attributes: SvgSerializerOptions["rootAttributes"]) {
  if (!attributes) return "";
  return Object.entries(attributes)
    .filter(
      ([name]) =>
        /^(?:class|id|role|tabindex)$/.test(name) || /^(?:aria|data)-[a-z0-9_.:-]+$/.test(name),
    )
    .map(([name, value]) => attribute(name, value))
    .join("");
}

function textAnchor(align: GraphicText["align"]) {
  if (align === "center") return "middle";
  if (align === "right") return "end";
  return "start";
}

function dominantBaseline(verticalAlign: GraphicText["verticalAlign"]) {
  if (verticalAlign === "middle") return "middle";
  if (verticalAlign === "bottom") return "text-after-edge";
  if (verticalAlign === "top") return "text-before-edge";
  return "alphabetic";
}

function serializeText(node: GraphicText) {
  const horizontalScale = node.horizontalScale ?? 1;
  const scaleTransform =
    horizontalScale === 1
      ? ""
      : `translate(${formatNumber(node.x)} 0) scale(${formatNumber(horizontalScale)} 1) translate(${formatNumber(-node.x)} 0)`;
  const lineHeight = node.lineHeight ?? node.fontSize * 1.2;
  const lines = node.value.split("\n");
  const content = lines
    .map((line, index) => {
      if (index === 0) return `<tspan x="${formatNumber(node.x)}">${escapeText(line)}</tspan>`;
      return `<tspan x="${formatNumber(node.x)}" dy="${formatNumber(lineHeight)}">${escapeText(line)}</tspan>`;
    })
    .join("");
  return `<text${commonAttributes(node, scaleTransform)}${attribute("x", node.x)}${attribute(
    "y",
    node.y,
  )}${attribute("text-anchor", textAnchor(node.align))}${attribute(
    "dominant-baseline",
    dominantBaseline(node.verticalAlign),
  )}${attribute("font-family", node.fontFamily)}${attribute("font-size", node.fontSize)}${attribute(
    "font-weight",
    node.fontWeight,
  )}${attribute("letter-spacing", node.letterSpacing)}${attribute("fill", node.colour)}${
    node.wrap && node.width !== undefined ? attribute("data-wrap-width", node.width) : ""
  }${strokeAttributes(node.stroke)}>${content}</text>`;
}

function imageAspectRatio(image: GraphicImage) {
  if (image.fit === "cover") return "xMidYMid slice";
  if (image.fit === "fill" || image.fit === "none") return "none";
  return "xMidYMid meet";
}

function serializeNode(node: GraphicNode, context: SerializerContext): string {
  if (node.type === "group") {
    return `<g${commonAttributes(node)}>${node.children
      .map((child) => serializeNode(child, context))
      .join("")}</g>`;
  }
  if (node.type === "rect") {
    return `<rect${commonAttributes(node)}${attribute("x", node.x)}${attribute(
      "y",
      node.y,
    )}${attribute("width", node.width)}${attribute("height", node.height)}${attribute(
      "rx",
      node.radius,
    )}${fillAttributes(node.fill, context.gradients)}${strokeAttributes(node.stroke)}/>`;
  }
  if (node.type === "ellipse") {
    return `<ellipse${commonAttributes(node)}${attribute("cx", node.cx)}${attribute(
      "cy",
      node.cy,
    )}${attribute("rx", node.radiusX)}${attribute("ry", node.radiusY)}${fillAttributes(
      node.fill,
      context.gradients,
    )}${strokeAttributes(node.stroke)}/>`;
  }
  if (node.type === "line") {
    return `<line${commonAttributes(node)}${attribute("x1", node.from.x)}${attribute(
      "y1",
      node.from.y,
    )}${attribute("x2", node.to.x)}${attribute("y2", node.to.y)}${strokeAttributes(node.stroke)}/>`;
  }
  if (node.type === "polyline") {
    const points = node.points.map(({ x, y }) => `${formatNumber(x)},${formatNumber(y)}`).join(" ");
    const tag = node.closed ? "polygon" : "polyline";
    return `<${tag}${commonAttributes(node)}${attribute("points", points)}${fillAttributes(
      node.fill,
      context.gradients,
    )}${strokeAttributes(node.stroke)}/>`;
  }
  if (node.type === "path") {
    return `<path${commonAttributes(node)}${attribute("d", node.data)}${fillAttributes(
      node.fill,
      context.gradients,
    )}${strokeAttributes(node.stroke)}/>`;
  }
  if (node.type === "text") return serializeText(node);
  const href = context.resolveAsset?.(node.assetId);
  if (!href) {
    return `<rect${commonAttributes(node)}${attribute("x", node.x)}${attribute(
      "y",
      node.y,
    )}${attribute("width", node.width)}${attribute("height", node.height)} fill="none" stroke="#777" stroke-width="0.25" data-missing-asset="${escapeAttribute(
      node.assetId,
    )}"/>`;
  }
  return `<image${commonAttributes(node)}${attribute("href", href)}${attribute(
    "x",
    node.x,
  )}${attribute("y", node.y)}${attribute("width", node.width)}${attribute(
    "height",
    node.height,
  )}${attribute("preserveAspectRatio", imageAspectRatio(node))}/>`;
}

export function serializeGraphicTreeToSvg(tree: GraphicTree, options: SvgSerializerOptions) {
  if (!Number.isFinite(options.width) || options.width <= 0) {
    throw new Error("SVG width must be a positive finite number.");
  }
  if (!Number.isFinite(options.height) || options.height <= 0) {
    throw new Error("SVG height must be a positive finite number.");
  }
  const idPrefix = options.idPrefix ?? "graphic";
  const fills: GraphicFill[] = [];
  collectGradientFills(tree, fills);
  const uniqueKeys = [...new Set(fills.map(gradientKey))];
  const gradients = new Map(uniqueKeys.map((key, index) => [key, `${idPrefix}-gradient-${index}`]));
  const fillByKey = new Map(fills.map((fill) => [gradientKey(fill), fill]));
  const definitions = uniqueKeys
    .map((key) => serializeGradient(fillByKey.get(key)!, gradients.get(key)!))
    .join("");
  const viewBox = options.viewBox ?? { x: 0, y: 0, width: options.width, height: options.height };
  const unit = options.unit ?? "mm";
  const metadata = `${options.title ? `<title>${escapeText(options.title)}</title>` : ""}${
    options.description ? `<desc>${escapeText(options.description)}</desc>` : ""
  }`;
  return `<svg xmlns="http://www.w3.org/2000/svg"${serializeRootAttributes(options.rootAttributes)} width="${formatNumber(options.width)}${unit}" height="${formatNumber(options.height)}${unit}" viewBox="${formatNumber(viewBox.x)} ${formatNumber(viewBox.y)} ${formatNumber(viewBox.width)} ${formatNumber(viewBox.height)}">${metadata}${definitions ? `<defs>${definitions}</defs>` : ""}${serializeNode(
    tree,
    {
      gradients,
      resolveAsset: options.resolveAsset,
    },
  )}</svg>`;
}

export const svgAdapterInternals = {
  escapeAttribute,
  escapeText,
  formatNumber,
  gradientKey,
};
